import type { Category } from '@/app/types/item';
import { cn } from '@/lib/utils';

interface CategoryChipsProps {
    categories: Category[];
    selectedCategoryId: number | null;
    onSelect: (categoryId: number | null) => void;
    /** Hidden when the party has no order history to repeat. */
    showUsualChip: boolean;
}

/**
 * Horizontally scrolling, single-select category filter.
 *
 * The leading chip is "Usually ordered" (null category), which is the default view —
 * a manager standing in a familiar store should not have to search for what that
 * store always buys.
 */
export function CategoryChips({
    categories,
    selectedCategoryId,
    onSelect,
    showUsualChip,
}: CategoryChipsProps) {
    const chip = (label: string, value: number | null) => {
        const active = selectedCategoryId === value;
        return (
            <button
                key={value ?? 'usual'}
                type="button"
                onClick={() => onSelect(value)}
                aria-pressed={active}
                className={cn(
                    'flex-shrink-0 px-4 h-9 rounded-full text-sm font-medium whitespace-nowrap',
                    'transition-colors border',
                    active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-secondary text-secondary-foreground border-transparent active:bg-accent'
                )}
            >
                {label}
            </button>
        );
    };

    return (
        <div
            className="flex gap-2 overflow-x-auto px-4 py-2"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        >
            {showUsualChip && chip('Usually ordered', null)}
            {categories.map((category) => chip(category.name, category.id))}
        </div>
    );
}
